import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, PackageX, Receipt, CheckCheck, ShoppingCart, FileText } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { ROUTES } from '@/lib/config/routes';
import { formatRelative } from '@/lib/utils/format';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/useNotifications';
import type { AppNotification } from '../repository';

const TYPE_ICON: Record<string, { icon: typeof Bell; className: string }> = {
  payment_evidence: {
    icon: Receipt,
    className: 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300',
  },
  low_stock: {
    icon: PackageX,
    className: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
  },
  sale_created: {
    icon: ShoppingCart,
    className: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  quote_created: {
    icon: FileText,
    className: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  },
  order_created: {
    icon: ShoppingCart,
    className: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
  },
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = data?.data ?? [];
  const unread = data?.unread ?? 0;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const routeForNotification = (n: AppNotification): string | null => {
    if (n.type === 'low_stock' || n.refType === 'product') return ROUTES.inventory;
    if (n.type === 'sale_created') return ROUTES.sales;
    if (n.type === 'quote_created') return ROUTES.quotes;
    if (n.type === 'order_created') return ROUTES.orders;
    return null;
  };

  const onItemClick = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id);
    const to = routeForNotification(n);
    if (to) {
      setOpen(false);
      navigate(to);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-lg p-2 text-content-muted transition hover:text-content"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'z-30 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-lg animate-fade-in',
            'fixed left-1/2 top-14 mt-1.5 w-[calc(100vw-1.5rem)] max-w-sm -translate-x-1/2',
            'sm:absolute sm:left-auto sm:right-0 sm:top-full sm:w-80 sm:max-w-none sm:translate-x-0',
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <p className="text-sm font-semibold text-content">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="flex items-center gap-1 text-xs font-medium text-brand-600 transition hover:text-brand-700 dark:text-brand-400"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto h-7 w-7 text-content-subtle" />
                <p className="mt-2 text-sm text-content-muted">No notifications</p>
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_ICON[n.type] ?? {
                  icon: Bell,
                  className: 'bg-surface-muted text-content-muted',
                };
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => onItemClick(n)}
                    className={cn(
                      'flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition hover:bg-surface-muted',
                      !n.read && 'bg-brand-50/40 dark:bg-brand-900/10',
                    )}
                  >
                    <div
                      className={cn(
                        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        meta.className,
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-content">{n.title}</p>
                        {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand-600" />}
                      </div>
                      <p className="mt-0.5 text-xs text-content-muted">{n.message}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-content-subtle">
                        {formatRelative(n.createdAt)}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
